import type { ClientRow, ClientContact } from '../context/ClientManagementContext';
import type { ClientFormData } from '../components/ClientDrawer/schema';
import backendClientsCrud from '@services/housien/clients';
import { extractClientDetailExtras } from '@/shared/utils/extract-client-detail-extras';
import { effectiveClientRoles } from '../utils/client-roles';

/**
 * Fetches a client by id, falling back to the company type on failure.
 *
 * @param agencyId - The agency identifier.
 * @param clientId - The client identifier.
 * @returns The raw client payload from the backend.
 */
export async function fetchClientWithFallback(
  agencyId: string,
  clientId: string,
): Promise<Record<string, unknown>> {
  const crud = backendClientsCrud(agencyId);
  return await crud.getById(clientId) as Record<string, unknown>;
}

type NestedSections = {
  metadata: Record<string, unknown>;
  personalInfo: Record<string, unknown>;
  identity: Record<string, unknown>;
  contacts: ClientContact[];
  demandProfile: Record<string, unknown>;
  primaryBroker: string;
  capturedBy: string;
  externalCaptor: string;
  externalBroker: string;
  originChannel: string;
  originDetail: string;
  originCampaign: string;
};

/**
 * Reads nested client sections from an API payload.
 *
 * @param c - The raw nested client payload.
 * @returns The normalized nested section bags.
 */
function readNestedSections(c: Record<string, unknown>): NestedSections {
  const assignment = (c.assignment as Record<string, unknown>) ?? {};
  const origin = (c.origin as Record<string, unknown>) ?? {};
  return {
    metadata: (c.metadata as Record<string, unknown>) ?? {},
    personalInfo: (c.personal_info as Record<string, unknown>) ?? {},
    identity: (c.identity as Record<string, unknown>) ?? {},
    contacts: (assignment.contacts as ClientContact[]) ?? [],
    demandProfile: (c.demand_profile as Record<string, unknown>) ?? {},
    primaryBroker: (assignment.primary_broker as string) ?? '',
    capturedBy: (assignment.captured_by as string) ?? '',
    externalCaptor: (assignment.external_captor as string) ?? '',
    externalBroker: (assignment.external_broker as string) ?? '',
    originChannel: (origin.channel as string) ?? '',
    originDetail: (origin.source_detail as string) ?? '',
    originCampaign: (origin.campaign as string) ?? '',
  };
}

/**
 * Resolves display email/phone from typed contact entries.
 *
 * @param contacts - Contact entries from the assignment section.
 * @returns Email and phone display strings.
 */
function resolveContactChannels(
  contacts: Array<{ type: string; value: string }>,
): { email: string; phone: string } {
  const email = contacts.find((ct) => ct.type === 'email')?.value ?? '';
  const phone = contacts.find((ct) => ct.type === 'phone')?.value
    ?? contacts.find((ct) => ct.type === 'whatsapp')?.value
    ?? '';
  return { email, phone };
}

/**
 * Converts a blank string to undefined for optional ClientRow fields.
 *
 * @param value - Raw string that may be empty.
 * @returns The value, or undefined when blank.
 */
function blankToUndef(value: string | undefined): string | undefined {
  return value ? value : undefined;
}

/**
 * Maps extras from extractClientDetailExtras onto optional ClientRow fields.
 *
 * @param extras - Extracted detail extras.
 * @returns Partial ClientRow extras fields.
 */
function mapExtrasFields(
  extras: ReturnType<typeof extractClientDetailExtras>,
): Partial<ClientRow> {
  return {
    nationality: blankToUndef(extras.nationality),
    profession: blankToUndef(extras.profession),
    marital_status: blankToUndef(extras.marital_status),
    rg: blankToUndef(extras.rg),
    issuing_agency: blankToUndef(extras.issuing_agency),
    trade_name: blankToUndef(extras.trade_name),
    corporate_name: blankToUndef(extras.corporate_name),
    address: blankToUndef(extras.address),
    address_fields: extras.address_fields,
    monthly_income: extras.monthly_income,
    income_proof: blankToUndef(extras.income_proof),
  };
}

/**
 * Reads one optional text off a payload block.
 *
 * @param source - The block (personal_info, metadata...).
 * @param key - The field name.
 * @returns The trimmed text, or undefined when blank or absent.
 */
function optionalText(source: Record<string, unknown>, key: string): string | undefined {
  const value = source[key];
  return typeof value === 'string' && value.trim() ? value.trim() : undefined;
}

/**
 * Maps the attributes of plan 2026-10-28 onto optional ClientRow fields.
 *
 * @param sections - The nested section bags.
 * @returns The ClientRow attribute fields.
 */
function mapAttributeFields(sections: NestedSections): Partial<ClientRow> {
  const { personalInfo, metadata, demandProfile } = sections;
  return {
    gender: optionalText(personalInfo, 'gender'),
    education: optionalText(personalInfo, 'education'),
    job_title: optionalText(personalInfo, 'job_title'),
    employer: optionalText(personalInfo, 'employer'),
    industry: optionalText(personalInfo, 'industry'),
    legacy_code: optionalText(metadata, 'legacy_code'),
    client_roles: effectiveClientRoles(demandProfile),
    captured_by: sections.capturedBy || undefined,
    external_captor: sections.externalCaptor || undefined,
    external_broker: sections.externalBroker || undefined,
    origin_campaign: sections.originCampaign || undefined,
  };
}

/**
 * Maps a nested API client payload into the ClientRow shape used by the detail page.
 *
 * @param c - The raw client payload from the backend (nested model).
 * @returns The normalized client row.
 */
export function mapResponseToClientRow(c: Record<string, unknown>): ClientRow {
  const sections = readNestedSections(c);
  const {
    metadata, personalInfo, identity, contacts, demandProfile, primaryBroker, originChannel,
    originDetail,
  } = sections;
  const { email, phone } = resolveContactChannels(contacts);
  const extras = extractClientDetailExtras(c);

  return {
    client_id: (metadata.client_id as string) ?? '',
    full_name: (personalInfo.full_name as string) ?? (identity.corporate_name as string) ?? '',
    email,
    phone,
    status: (metadata.status as string) ?? 'active',
    type_of_client: (identity.person_type as string) ?? 'individual',
    last_contact_date: (metadata.last_contact_date as string) ?? '',
    preferences: demandProfile,
    contacts: mapContacts(contacts),
    lead_origin: mapLeadOrigin(demandProfile.lead_origin as Record<string, unknown> | undefined),
    lead_interactions: mapLeadInteractions(
      demandProfile.lead_interactions as Record<string, unknown>[] | undefined,
    ),
    cpf: (identity.cpf as string) ?? '',
    cnpj: (identity.cnpj as string) ?? '',
    birth_date: (personalInfo.birth_date as string) ?? '',
    ...mapExtrasFields(extras),
    notes: (metadata.notes as string) ?? '',
    registration_date: (metadata.registration_date as string) ?? '',
    primary_broker: primaryBroker || undefined,
    origin_channel: originChannel || undefined,
    origin_detail: originDetail || undefined,
    ...mapAttributeFields(sections),
  };
}

/**
 * Builds contact entries from a client row's separate email/phone fields.
 */
function contactsFromEmailPhone(c: ClientRow): Array<{ type: string; value: string }> {
  const contacts: Array<{ type: string; value: string }> = [];
  if (c.email) contacts.push({ type: 'email', value: c.email });
  if (c.phone) contacts.push({ type: 'phone', value: c.phone });
  return contacts;
}

/**
 * Extracts contact entries from a client row.
 *
 * @param c - The loaded client row.
 * @returns The client's contacts, or a blank WhatsApp entry when it has none.
 */
function extractEditContacts(c: ClientRow): Array<{ type: string; value: string; label?: string }> {
  if (Array.isArray(c.contacts) && c.contacts.length > 0) {
    return c.contacts.map((ct) => ({ type: ct.type, value: ct.value, ...(ct.label ? { label: ct.label } : {}) }));
  }
  const contacts = contactsFromEmailPhone(c);
  return contacts.length > 0 ? contacts : [{ type: 'whatsapp', value: '' }];
}

/**
 * Builds identity form defaults for company or individual clients.
 *
 * @param c - The loaded client row.
 * @param isCompany - Whether the client is a company.
 * @returns Identity section defaults.
 */
function buildEditIdentity(c: ClientRow, isCompany: boolean) {
  if (isCompany) {
    return {
      personType: 'company' as const,
      cpf: '',
      rg: '',
      issuingAgency: '',
      cnpj: c.cnpj ?? '',
      corporateName: c.corporate_name || c.full_name,
      tradeName: c.trade_name ?? '',
    };
  }
  return {
    personType: 'individual' as const,
    cpf: c.cpf ?? '',
    rg: c.rg ?? '',
    issuingAgency: c.issuing_agency ?? '',
    cnpj: '',
    corporateName: '',
    tradeName: '',
  };
}

/**
 * Builds address form defaults from structured address fields.
 *
 * @param fields - Optional structured address fields.
 * @returns Address defaults, or undefined when absent.
 */
function buildEditAddress(fields: ClientRow['address_fields']) {
  if (!fields) return undefined;
  return {
    street: fields.street,
    number: fields.number,
    complement: fields.complement,
    neighborhood: fields.neighborhood,
    city: fields.city,
    state: fields.state,
    zipCode: fields.zip_code,
  };
}

/**
 * Builds drawer default form values from a loaded client row.
 *
 * @param c - The loaded client row.
 * @returns Partial form defaults for the edit drawer.
 */
/** Demand-profile keys whose stored value is a list of chip values. */
const DEMAND_LIST_FIELDS = [
  ['propertyKinds', 'property_kinds'],
  ['cities', 'cities'],
  ['neighborhoods', 'neighborhoods'],
  ['amenitiesInternal', 'amenities_internal'],
  ['amenitiesExternal', 'amenities_external'],
  ['amenitiesSecurity', 'amenities_security'],
  ['condoLeisure', 'condo_leisure'],
  ['condoSecurity', 'condo_security'],
  ['tags', 'tags'],
] as const;

/** Demand-profile keys stored as numbers and edited as strings. */
const DEMAND_NUMERIC_FIELDS = [
  ['priceMin', 'price_min'],
  ['priceMax', 'price_max'],
  ['bedroomsMin', 'bedrooms_min'],
  ['areaMin', 'area_min'],
  ['bathroomsMin', 'bathrooms_min'],
  ['parkingMin', 'parking_min'],
  ['suitesMin', 'suites_min'],
] as const;

/**
 * Reads a stored list value as a string array.
 *
 * @param raw - The stored value.
 * @returns The chip values, or an empty list when absent or malformed.
 */
function readChipList(raw: unknown): string[] {
  return Array.isArray(raw) ? raw.map(String) : [];
}

/**
 * Formats a stored numeric preference for a text input.
 *
 * @param raw - The stored value.
 * @param asCurrency - Whether to group thousands the way the price mask does.
 * @returns The display string, or empty when the preference is unset.
 */
function readNumericPreference(raw: unknown, asCurrency: boolean): string {
  const value = raw === null || raw === '' ? Number.NaN : Number(raw);
  if (Number.isNaN(value)) return '';
  return asCurrency ? value.toLocaleString('pt-BR') : String(value);
}

/**
 * Rebuilds the drawer's demand-profile form values from the stored record.
 *
 * Without this the edit drawer opened with every preference blank, and
 * saving wrote those blanks over what the broker had already recorded.
 *
 * @param preferences - The stored demand_profile bag.
 * @returns The form-shaped demand profile.
 */
function buildEditDemandProfile(
  preferences: Record<string, unknown>,
): NonNullable<ClientFormData['demandProfile']> {
  const profile: Record<string, unknown> = {
    businessType: (preferences.business_type as string) ?? '',
    furnished: (preferences.furnished as string) ?? '',
    petFriendly: petPreferenceFrom(preferences.pet_friendly),
  };
  for (const [formKey, storedKey] of DEMAND_LIST_FIELDS) {
    profile[formKey] = readChipList(preferences[storedKey]);
  }
  for (const [formKey, storedKey] of DEMAND_NUMERIC_FIELDS) {
    profile[formKey] = readNumericPreference(preferences[storedKey], storedKey.startsWith('price'));
  }
  return profile as NonNullable<ClientFormData['demandProfile']>;
}

/**
 * Maps the stored pet flag onto the form's tri-state.
 *
 * @param raw - The stored boolean, or undefined when never answered.
 * @returns 'yes', 'no', or '' when the question was left open.
 */
function petPreferenceFrom(raw: unknown): string {
  if (raw === true) return 'yes';
  if (raw === false) return 'no';
  return '';
}

/**
 * Build edit default values.
 */
export function buildEditDefaultValues(c: ClientRow): Partial<ClientFormData> {
  const isCompany = c.type_of_client === 'company';
  return {
    personalInfo: {
      fullName: c.full_name,
      birthDate: (!isCompany && c.birth_date) ? c.birth_date : '',
      maritalStatus: c.marital_status ?? '',
      nationality: c.nationality ?? '',
      profession: c.profession ?? '',
      gender: c.gender ?? '',
      education: c.education ?? '',
      jobTitle: c.job_title ?? '',
      employer: c.employer ?? '',
      industry: c.industry ?? '',
    },
    identity: buildEditIdentity(c, isCompany),
    primaryBroker: c.primary_broker ?? '',
    clientRoles: c.client_roles ?? [],
    capturedBy: c.captured_by ?? '',
    legacyCode: c.legacy_code ?? '',
    origin: {
      channel: c.origin_channel ?? '',
      sourceDetail: c.origin_detail ?? '',
      campaign: c.origin_campaign ?? '',
    },
    contacts: extractEditContacts(c),
    address: buildEditAddress(c.address_fields),
    financial: {
      monthlyIncome: c.monthly_income != null
        ? c.monthly_income.toLocaleString('pt-BR')
        : '',
      incomeProof: c.income_proof ?? '',
    },
    demandProfile: buildEditDemandProfile(c.preferences ?? {}),
  };
}

/**
 * Normalizes a raw contacts array into typed client contacts.
 *
 * @param raw - The raw contacts value.
 * @returns The typed contacts, or undefined when not an array.
 */
function mapContacts(raw: unknown): ClientContact[] | undefined {
  if (!Array.isArray(raw)) return undefined;
  return (raw as Record<string, unknown>[]).map((ct) => ({
    type: (ct.type as string) ?? '',
    value: (ct.value as string) ?? '',
    label: ct.label as string | undefined,
    handle: ct.handle as string | undefined,
  }));
}

/**
 * Normalizes a raw lead origin record into the typed shape.
 *
 * @param raw - The raw lead origin record.
 * @returns The typed lead origin, or undefined when absent.
 */
function mapLeadOrigin(raw: Record<string, unknown> | undefined) {
  if (!raw) return undefined;
  const socialAccounts = Array.isArray(raw.social_accounts)
    ? (raw.social_accounts as Record<string, unknown>[])
        .filter((a) => a.platform && a.external_user_id)
        .map((a) => ({
          platform: String(a.platform),
          external_user_id: String(a.external_user_id),
        }))
    : undefined;
  return {
    platform: (raw.platform as string) ?? '',
    source: (raw.source as string) ?? '',
    external_user_id: (raw.external_user_id as string) ?? '',
    first_captured_at: (raw.first_captured_at as string) ?? '',
    social_accounts: socialAccounts,
  };
}

/**
 * Normalizes a raw lead interactions array into typed interactions.
 *
 * @param raw - The raw interactions value.
 * @returns The typed interactions, or undefined when not an array.
 */
function mapLeadInteractions(raw: Record<string, unknown>[] | undefined) {
  if (!Array.isArray(raw)) return undefined;
  return raw.map((i) => ({
    type: (i.type as string) ?? '',
    text: i.text as string | undefined,
    attachment_type: i.attachment_type as string | undefined,
    reaction_type: i.reaction_type as string | undefined,
    property_id: i.property_id as string | undefined,
    publication_id: i.publication_id as string | undefined,
    permalink: i.permalink as string | undefined,
    captured_at: (i.captured_at as string) ?? '',
    platform: i.platform as string | undefined,
  }));
}
